import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorGroupCodeField from '../../components/SupervisorGroupCodeField';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import LotterySearchModal from '../../components/LotterySearchModal';
import {
  createSupervisorProductConfig,
  listSupervisorProductConfigs,
  updateSupervisorProductConfig,
} from '../../api/supervisor';
import { listProducts } from '../../api/products';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import './SupervisorCentralPage.css';
import './SupervisorProductConfigPage.css';

// Memes aides UX que la version company (ParametresLoterieResultsView.jsx) —
// pas de raison de se comporter differemment pour le superviseur.
const AUTOFILL_FOLLOWERS = { L41: ['L42', 'L43'], L51: ['L52', 'L53'] };
// Commission de vente et Épargnes % : independants, peuvent rester vides (-> 0).
const pctOrZero = (v) => (String(v ?? '').trim() === '' ? '0' : v);

const SupervisorProductConfigPage = () => {
  const [scopeType, setScopeType] = useState('Groupe'); // 'Groupe' | 'Banque' — pas de 'Tout', hors du domaine d'un superviseur.
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);

  const [rows, setRows] = useState(null); // null = pas encore cherche
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleScopeTypeChange = (type) => {
    setScopeType(type);
    setScopeId('');
    setScopeName('');
    setRows(null);
  };

  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '') { setLotteryLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res && res.code !== '0') {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!scopeId || !scopeName || !lottery) return;
    setLoadingRows(true);
    setError('');
    setSaved(false);
    const scope = scopeType === 'Groupe' ? { group: scopeId } : { bank: scopeId };
    try {
      const [products, configs] = await Promise.all([
        listProducts(),
        listSupervisorProductConfigs({ tirage: lottery, ...scope }),
      ]);
      const configByProduct = Object.fromEntries(configs.map((c) => [c.product, c]));
      setRows(products.map((p) => {
        const cfg = configByProduct[p.id];
        return {
          productId: p.id,
          name: p.name,
          abbr: p.abbr,
          configId: cfg ? cfg.id : null,
          prixPoints: cfg ? cfg.points_price : '0',
          limiteEnLigne: cfg ? cfg.online_limit : '0',
          limiteLocal: cfg ? cfg.local_limit : '0',
          commissionVente: cfg ? cfg.sales_commission : '0',
          epargnesPct: cfg ? cfg.savings_pct : '0',
          keys: p.payout_keys.map((k) => {
            const payout = cfg?.payouts.find((pay) => pay.key === k.id);
            return { keyId: k.id, key: k.key, value: payout ? payout.won_per_point : '0' };
          }),
        };
      }));
    } catch (err) {
      setError('Impossible de charger les produits/configurations.');
    } finally {
      setLoadingRows(false);
    }
  };

  const updateRowField = (rowIndex, field, value) => {
    setRows((prev) => {
      const followers = AUTOFILL_FOLLOWERS[prev[rowIndex].abbr] || [];
      return prev.map((r, i) => (
        (i === rowIndex || followers.includes(r.abbr)) ? { ...r, [field]: value } : r
      ));
    });
    setSaved(false);
  };

  const updateKeyValue = (rowIndex, keyIndex, value) => {
    setRows((prev) => {
      const edited = prev[rowIndex];
      const followers = AUTOFILL_FOLLOWERS[edited.abbr] || [];
      return prev.map((r, i) => {
        if (i === rowIndex) {
          return { ...r, keys: r.keys.map((k, ki) => (ki === keyIndex ? { ...k, value } : k)) };
        }
        if (followers.includes(r.abbr)) {
          // Recopie PAR POSITION (pas par nom de cle) : L41/L42/L43 ont chacun
          // une seule cle mais de nom different (1st / 2nd / 3rd), idem L51/L52/L53
          // — la comparaison par nom ne recopiait donc jamais "Gagne par point".
          return { ...r, keys: r.keys.map((k, ki) => (ki === keyIndex ? { ...k, value } : k)) };
        }
        return r;
      });
    });
    setSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const scope = scopeType === 'Groupe' ? { group: scopeId, bank: null } : { group: null, bank: scopeId };
    try {
      const updated = await Promise.all(rows.map((row) => {
        const payload = {
          tirage: Number(lottery),
          product: row.productId,
          ...scope,
          points_price: row.prixPoints,
          online_limit: row.limiteEnLigne,
          local_limit: row.limiteLocal,
          sales_commission: pctOrZero(row.commissionVente),
          savings_pct: pctOrZero(row.epargnesPct),
          payouts: row.keys.map((k) => ({ key: k.keyId, won_per_point: k.value })),
        };
        return row.configId
          ? updateSupervisorProductConfig(row.configId, payload)
          : createSupervisorProductConfig(payload);
      }));
      setRows((prev) => prev.map((r, i) => ({ ...r, configId: updated[i].id })));
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible — verifie les valeurs saisies.');
    } finally {
      setSaving(false);
    }
  };


  return (
    <SupervisorLayout>
      <p className="svc-breadcrumb">
        Ruta : <Link to="/supervisor/dashboard" className="svc-link">Menu</Link>
        {' >> '}
        <Link to="/supervisor/controles" className="svc-link">Controles</Link>
        {' >> '}
        <span>Parametres de loterie</span>
      </p>
      <h2 className="svpc-title">Parametres de loterie</h2>

      <div className="svpc-card">
        <form onSubmit={handleSearch} className="svpc-search-form">
          <div className="svpc-radio-row">
            {['Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="svpc-radio-label">
                <input
                  type="radio"
                  name="scopeType"
                  checked={scopeType === opt}
                  onChange={() => handleScopeTypeChange(opt)}
                />
                {opt}
              </label>
            ))}
          </div>

          <div className="svpc-row">
            <label className="svpc-label">{scopeType}</label>
            {scopeType === 'Groupe' ? (
              <SupervisorGroupCodeField
                value={scopeId}
                name={scopeName}
                onChange={(id) => { setScopeId(id); setScopeName(''); setRows(null); }}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); setRows(null); }}
                inputClass="svpc-input svpc-input-sm"
                iconClass="svpc-icon-btn"
                nameClass="svpc-scope-name"
                errClass="svpc-lookup-err"
              />
            ) : (
              <SupervisorBankCodeField
                value={scopeId}
                name={scopeName}
                onChange={(id) => { setScopeId(id); setScopeName(''); setRows(null); }}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); setRows(null); }}
                inputClass="svpc-input svpc-input-sm"
                iconClass="svpc-icon-btn"
                nameClass="svpc-scope-name"
                errClass="svpc-lookup-err"
              />
            )}
          </div>

          <div className="svpc-row">
            <label className="svpc-label">Loterie</label>
            <input
              className="svpc-input svpc-input-sm"
              type="text"
              inputMode="numeric"
              value={lottery}
              onChange={(e) => { setLottery(e.target.value); setLotteryLookupError(''); }}
              onBlur={handleLotteryBlur}
            />
            <span className="svpc-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
            {lotteryLookupError
              ? <span className="svpc-lookup-err">{lotteryLookupError}</span>
              : lotteryName && <span className="svpc-scope-name">{lotteryName}</span>}
          </div>

          {showLotteryModal && (
            <LotterySearchModal
              onSelect={(id, name) => {
                setLottery(String(id));
                setLotteryName(name || '');
                setLotteryLookupError('');
              }}
              onClose={() => setShowLotteryModal(false)}
            />
          )}

          <button type="submit" className="svpc-search-btn" disabled={!scopeId || !scopeName || !lottery || loadingRows}>
            {loadingRows ? 'Chargement...' : 'Chercher'}
          </button>
        </form>
      </div>

      {rows && (
        <form onSubmit={handleSave} className="svpc-card svpc-results-card">
          <p className="svpc-help">
            Les valeurs saisies sur <strong>L41</strong> se recopient automatiquement sur{' '}
            <strong>L42</strong>/<strong>L43</strong> (idem <strong>L51</strong> → <strong>L52</strong>/
            <strong>L53</strong>). <strong>Commission de vente</strong> et <strong>Épargnes %</strong> sont
            indépendants et peuvent rester vides (= 0).
          </p>

          {rows.map((row, gi) => (
            <div key={row.productId} className="svpc-group">
              <p className="svpc-group-name">{row.name}</p>
              <table className="svpc-table">
                <thead>
                  <tr>
                    <th>Prix Points</th>
                    <th>Limite en ligne</th>
                    <th>Limite Local</th>
                    <th>Commission de vente</th>
                    <th>Epargnes %</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><input className="svpc-cell-input" value={row.prixPoints} onChange={(e) => updateRowField(gi, 'prixPoints', e.target.value)} /></td>
                    <td><input className="svpc-cell-input" value={row.limiteEnLigne} onChange={(e) => updateRowField(gi, 'limiteEnLigne', e.target.value)} /></td>
                    <td><input className="svpc-cell-input" value={row.limiteLocal} onChange={(e) => updateRowField(gi, 'limiteLocal', e.target.value)} /></td>
                    <td><input className="svpc-cell-input" value={row.commissionVente} onChange={(e) => updateRowField(gi, 'commissionVente', e.target.value)} /></td>
                    <td><input className="svpc-cell-input" value={row.epargnesPct} onChange={(e) => updateRowField(gi, 'epargnesPct', e.target.value)} /></td>
                  </tr>
                </tbody>
              </table>

              {row.keys.length > 0 && (
                <table className="svpc-key-table">
                  <thead><tr><th>Kle</th><th>Gagne par point</th></tr></thead>
                  <tbody>
                    {row.keys.map((k, ki) => (
                      <tr key={k.keyId}>
                        <td>{k.key}</td>
                        <td><input className="svpc-cell-input" value={k.value} onChange={(e) => updateKeyValue(gi, ki, e.target.value)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}

          {error && <p className="svpc-error">{error}</p>}
          {saved && <p className="svpc-saved">Sauvegarde.</p>}

          <button type="submit" className="svpc-save-btn" disabled={saving}>
            {saving ? 'Sauvegarde...' : 'Sauver'}
          </button>
        </form>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorProductConfigPage;
